import { useState, useEffect, useCallback } from "react";
import { Badge, IconButton } from "@compass/core";
import { useTheme } from "@mui/material/styles";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import ApiService from "services/apiService";
import { NotificationDto } from "generated/openApi";
import { AppTopBarNotificationsModal } from "./AppTopBarNotificationsModal";

const INITIAL_PAGE_SIZE = 20;

export const AppTopBarNotifications = () => {
    const [unreadCount, setUnreadCount] = useState<number>(0);
    const [notifications, setNotifications] = useState<NotificationDto[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const theme = useTheme();

    const fetchUnreadCount = useCallback(async () => {
        try {
            const response = await ApiService.notifications.getList({
                unreadOnly: true,
                page: 1,
                pageSize: 1,
            });
            setUnreadCount(response.totalCount);
        } catch (error) {
            console.error("Failed to fetch unread notification count:", error);
        }
    }, []);

    const fetchNotifications = useCallback(async () => {
        setLoading(true);
        try {
            const response = await ApiService.notifications.getList({
                page: 1,
                pageSize: INITIAL_PAGE_SIZE,
            });
            const allNotifications = response.notifications ?? [];
            const unread = allNotifications.filter((n) => !n.isRead);
            setNotifications(unread);
            setUnreadCount(unread.length);
        } catch (error) {
            console.error("Failed to fetch notifications:", error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchUnreadCount();
    }, [fetchUnreadCount]);

    const handleBellClick = async () => {
        setModalOpen(true);
        await fetchNotifications();
    };

    const handleModalClose = async () => {
        setModalOpen(false);
        if (unreadCount > 0) {
            try {
                await ApiService.notifications.markAllAsRead();
                setUnreadCount(0);
                setNotifications([]);
            } catch (error) {
                console.error("Failed to mark all notifications as read:", error);
            }
        }
    };

    return (
        <>
            <IconButton onClick={handleBellClick} color="inherit">
                <Badge
                    badgeContent={unreadCount}
                    color="error"
                    invisible={unreadCount === 0}
                >
                    <NotificationsNoneOutlinedIcon sx={{ color: theme.palette.text.disabled }} />
                </Badge>
            </IconButton>

            <AppTopBarNotificationsModal
                open={modalOpen}
                loading={loading}
                notifications={notifications}
                unreadCount={unreadCount}
                onClose={handleModalClose}
            />
        </>
    );
};